import { Button } from "@/atoms-react/button/Button.react";
import InputLabel from "@/atoms-react/input/Input.react";
import { Modal, ModalContent, type ModalProps } from "@/atoms-react/modal/index.react";
import { Info } from "@/atoms-react/text/Text.react";
import { useKypay } from "@/features/kypay/hooks/use-kypay";
import type { WalletData } from "@/features/kypay/types";
import { formatCurrencyInput, formatNumber } from "@/features/utils/functions";
import { InfoCard, InfoCardType } from "@/molecules/info-card/InfoCard.react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { WalletNumber } from "../wallet-number/wallet-number.react";

type ModalFeedKypayProps = {
  wallet?: WalletData;
  isPendingSendToKypay?: boolean;
  onSetWallet?: () => void;
  onValidated?: (amount: number) => void;
} & ModalProps;

export const ModalCryptoToBank = ({
  wallet,
  isPendingSendToKypay,
  onSetWallet = () => {},
  onValidated = () => {},
  ...props
}: ModalFeedKypayProps) => {
  const { t } = useTranslation(["wallet"]);
  const { walletPolygon2LausanneQuery } = useKypay();
  const [amountString, setAmountString] = useState<string>("");
  const [amount, setAmount] = useState<number>(0);
  const resetInputs = () => {
    setAmount(0);
    setAmountString(formatNumber(0));
  };
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    forceCursorPosition();
  }, [amountString]);
  const forceCursorPosition = () => {
    if (inputRef.current) {
      const inputLength = inputRef.current.value.length;
      inputRef.current.setSelectionRange(inputLength, inputLength); // Force cursor to the end on focus
    }
  };
  useEffect(() => {
    if (!props.open) {
      resetInputs();
    }
  }, [props]);

  return (
    <>
      {!wallet?.walletConnect ? (
        <Modal className="w-full max-w-lg sm:w-full" {...props}>
          <ModalContent title={t("kypay.modal_deposit_funds.label.transfer_offchain")}>
            <div className={"mt-4 opacity-1"}>
              <div className={"mt-4 mb-6 w-full"}>
                <InfoCard type={InfoCardType.warning}>
                  <Info className="text-white dark:text-white">
                    {t("kypay.modal_deposit_funds.label.warning")}
                  </Info>
                </InfoCard>
              </div>

              <div className={"mt-4 mb-6 w-full"}>
                <WalletNumber
                  wallet={wallet?.walletAddress ?? null}
                  onSetWallet={onSetWallet}
                  label={t("kypay.modal_deposit_funds.label.sending_wallet")}
                />
              </div>
              <div className={"my-4 w-full"}>
                <WalletNumber
                  wallet={walletPolygon2LausanneQuery().data?.WalletAddress ?? ""}
                  label={t("kypay.modal_deposit_funds.label.receiving_wallet")}
                  copy
                />
              </div>
            </div>
          </ModalContent>
        </Modal>
      ) : (
        <Modal className="w-full max-w-lg sm:w-full" {...props}>
          <ModalContent title={t("kypay.modal_deposit_funds.label.transfer_offchain")}>
            <div className={"mt-4 opacity-1"}>
              <div className={"mt-4 mb-6 w-full"}>
                <InputLabel
                  value={amountString}
                  onClick={forceCursorPosition}
                  onChange={(value: string) => {
                    if (value.length > 16) return;
                    const formattedValue = formatCurrencyInput(value);
                    const parsed = parseFloat(formattedValue);
                    if (!isNaN(parsed)) {
                      setAmount(parsed);
                      setAmountString(formatNumber(parsed));
                    } else {
                      setAmountString(formatNumber(0));
                    }
                  }}
                  suffixIcon={<span>€</span>}
                  type="text"
                  min="0.00"
                  max="10000.00"
                  step="0.01"
                  label={t("kypay.modal_transfer_fund.input.amount_label")}
                  placeholder={t("kypay.modal_transfer_fund.input.amount_placeholder")}
                />
              </div>

              <Button
                disabled={!wallet || !amount || amount <= 0}
                loading={isPendingSendToKypay}
                shortcut="enter"
                className="shrink-0"
                onClick={() => {
                  if (amount) onValidated(amount);
                }}
              >
                {t("kypay.modal_transfer_fund.button.send")}
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};
